import React from 'react';
import { Link } from 'react-router-dom';
import { Cloud, Mail, Phone, MapPin, Facebook, Instagram, Youtube, Linkedin, Twitter } from 'lucide-react';

const Footer = () => {
  return (
    <footer id="footer" className="bg-[#0B0F1A] border-t border-[#00E5FF]/20 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* Column 1 - Logo & Tagline */}
          <div>
            <div className="mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_talent-solutions-10/artifacts/02y8uuas_IMG_9047.JPG" 
                alt="CloudXAI Logo" 
                className="h-20 w-auto"
                style={{ mixBlendMode: 'lighten' }}
              />
            </div>
            <p className="text-[#38BDF8] mb-6 font-medium">Learn Cloud. Think AI. Build the Future.</p>
            <p className="text-sm text-[#9CA3AF]">
              Empowering careers and enterprises with cutting-edge technology solutions.
            </p>
          </div>

          {/* Column 2 - Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6 font-['Space_Grotesk']">Quick Links</h3>
            <ul className="space-y-3">
              {['Home', 'About Us', 'Services', 'Contact'].map((link) => (
                <li key={link}>
                  <button
                    onClick={() => {
                      const id = link.toLowerCase().replace(' ', '-');
                      const element = document.getElementById(id === 'home' ? 'hero' : id === 'about-us' ? 'why-choose' : id);
                      if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="text-[#9CA3AF] hover:text-[#00E5FF] transition-colors"
                  >
                    {link}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 - Services */}
          <div>
            <h3 className="text-lg font-bold mb-6 font-['Space_Grotesk']">Services</h3>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => {
                    const element = document.getElementById('career-consultation');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="text-[#9CA3AF] hover:text-[#00E5FF] transition-colors"
                >
                  Corporate Training
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    const element = document.getElementById('hire-talent');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="text-[#9CA3AF] hover:text-[#00E5FF] transition-colors"
                >
                  Recruitment
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    const element = document.getElementById('software');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="text-[#9CA3AF] hover:text-[#00E5FF] transition-colors"
                >
                  Software Development
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    const element = document.getElementById('software');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="text-[#9CA3AF] hover:text-[#00E5FF] transition-colors"
                >
                  IT Services
                </button>
              </li>
            </ul>
          </div>

          {/* Column 4 - Resources */}
          <div>
            <h3 className="text-lg font-bold mb-6 font-['Space_Grotesk']">Resources</h3>
            <ul className="space-y-3">
              <li><Link to="/blog" className="text-[#9CA3AF] hover:text-[#00E5FF] transition-colors">Knowledge Hub</Link></li>
              <li><Link to="/success-stories" className="text-[#9CA3AF] hover:text-[#00E5FF] transition-colors">Success Stories</Link></li>
              <li><Link to="/courses" className="text-[#9CA3AF] hover:text-[#00E5FF] transition-colors">Course Comparison</Link></li>
              <li><Link to="/faq" className="text-[#9CA3AF] hover:text-[#00E5FF] transition-colors">FAQ</Link></li>
              <li><Link to="/verify" className="text-[#9CA3AF] hover:text-[#00E5FF] transition-colors">Verify Certificate</Link></li>
              <li><Link to="/book-a-consultation" className="text-[#9CA3AF] hover:text-[#00E5FF] transition-colors">Book Free Session</Link></li>
            </ul>
          </div>

          {/* Column 5 - Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-6 font-['Space_Grotesk']">Contact Info</h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-[#00E5FF] flex-shrink-0 mt-0.5" />
                <div>
                  <a href="mailto:hr@cloudxai.org" className="text-[#9CA3AF] hover:text-[#00E5FF] transition-colors block">
                    hr@cloudxai.org
                  </a>
                  <a href="mailto:info@cloudxai.org" className="text-[#9CA3AF] hover:text-[#00E5FF] transition-colors block">
                    info@cloudxai.org
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-[#00E5FF] flex-shrink-0 mt-0.5" />
                <a href="tel:+919108479626" className="text-[#9CA3AF] hover:text-[#00E5FF] transition-colors">
                  +91 91084 79626
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-[#00E5FF] flex-shrink-0 mt-0.5" />
                <a href="https://cloudxai.org" target="_blank" rel="noopener noreferrer" className="text-[#9CA3AF] hover:text-[#00E5FF] transition-colors">
                  www.cloudxai.org
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Row - Social & Copyright */}
        <div className="border-t border-[#00E5FF]/20 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            {/* Social Icons */}
            <div className="flex items-center space-x-4">
              <a
                href="https://www.linkedin.com/feed/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border-2 border-[#00E5FF]/30 hover:border-[#00E5FF] hover:bg-[#00E5FF]/10 flex items-center justify-center transition-all duration-300 hover:scale-110 group"
              >
                <Linkedin className="w-5 h-5 text-[#9CA3AF] group-hover:text-[#00E5FF]" />
              </a>
              <a
                href="https://www.instagram.com/cloudxai.official"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border-2 border-[#00E5FF]/30 hover:border-[#00E5FF] hover:bg-[#00E5FF]/10 flex items-center justify-center transition-all duration-300 hover:scale-110 group"
              >
                <Instagram className="w-5 h-5 text-[#9CA3AF] group-hover:text-[#00E5FF]" />
              </a>
              <a
                href="https://www.facebook.com/cloudxai.official"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border-2 border-[#00E5FF]/30 hover:border-[#00E5FF] hover:bg-[#00E5FF]/10 flex items-center justify-center transition-all duration-300 hover:scale-110 group"
              >
                <Facebook className="w-5 h-5 text-[#9CA3AF] group-hover:text-[#00E5FF]" />
              </a>
              <a
                href="https://www.youtube.com/@CloudXAI.Official"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border-2 border-[#00E5FF]/30 hover:border-[#00E5FF] hover:bg-[#00E5FF]/10 flex items-center justify-center transition-all duration-300 hover:scale-110 group"
              >
                <Youtube className="w-5 h-5 text-[#9CA3AF] group-hover:text-[#00E5FF]" />
              </a>
              <a
                href="https://x.com/Cloudxai120847"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border-2 border-[#00E5FF]/30 hover:border-[#00E5FF] hover:bg-[#00E5FF]/10 flex items-center justify-center transition-all duration-300 hover:scale-110 group"
              >
                <Twitter className="w-5 h-5 text-[#9CA3AF] group-hover:text-[#00E5FF]" />
              </a>
            </div>

            {/* Copyright */}
            <p className="text-[#9CA3AF] text-sm">© {new Date().getFullYear()} CloudXAI Technologies. All Rights Reserved.</p>
          </div>
          <p className="text-[#9CA3AF]/60 text-xs text-center mt-6 max-w-4xl mx-auto leading-relaxed">
            CloudXAI Technologies is a private professional certification authority specializing in AI, Data, and Cloud technologies, issuing industry-oriented credentials verified through secure digital authentication.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
